import { useState, useCallback } from "react";

import { toast } from "@/components/ui/sonner";

import { useEdgeFunction } from "./useEdgeFunction";

interface StepUpCheckResult {
  requires_step_up: boolean;
  reason?: string;
  anomaly_score?: number;
  session_age_minutes?: number;
  session_id?: string;
}

interface UseStepUpAuthReturn {
  checkStepUpRequired: (actionType: string) => Promise<StepUpCheckResult | null>;
  requireStepUp: (actionType: string, onVerified: () => void | Promise<void>) => Promise<void>;
  showStepUpDialog: boolean;
  setShowStepUpDialog: (show: boolean) => void;
  currentActionType: string;
  stepUpData: StepUpCheckResult | null;
  handleVerified: () => Promise<void>;
}

/**
 * Hook for checking and enforcing step-up authentication for sensitive actions
 * 
 * Usage:
 * ```tsx
 * const { requireStepUp, showStepUpDialog, setShowStepUpDialog, currentActionType, stepUpData, handleVerified } = useStepUpAuth();
 * 
 * const handleSensitiveAction = async () => {
 *   await requireStepUp('change_password', async () => {
 *     // This only runs after step-up verification (if required)
 *     await actuallyChangePassword();
 *   });
 * };
 * 
 * // In your render:
 * <StepUpAuthDialog
 *   open={showStepUpDialog}
 *   onOpenChange={setShowStepUpDialog}
 *   onVerified={handleVerified}
 *   actionType={currentActionType}
 *   reason={stepUpData?.reason}
 *   anomalyScore={stepUpData?.anomaly_score}
 * />
 * ```
 */
export const useStepUpAuth = (): UseStepUpAuthReturn => {
  const [showStepUpDialog, setShowStepUpDialog] = useState(false);
  const [currentActionType, setCurrentActionType] = useState<string>('');
  const [stepUpData, setStepUpData] = useState<StepUpCheckResult | null>(null);
  const [pendingAction, setPendingAction] = useState<(() => void | Promise<void>) | null>(null);
  const { execute: invoke } = useEdgeFunction({ auth: true });

  const checkStepUpRequired = useCallback(async (actionType: string): Promise<StepUpCheckResult | null> => {
    try {
      const { data, error } = await invoke('check-step-up-auth', {
        body: { action_type: actionType },
        showErrorToast: false
      });

      if (error) {
        console.error('Error checking step-up requirement:', error);
        // Fail secure - assume step-up is required
        return {
          requires_step_up: true,
          reason: 'check_failed_fail_secure'
        };
      }

      return data;
    } catch (error) {
      console.error('Error checking step-up requirement:', error);
      return {
        requires_step_up: true,
        reason: 'check_failed_fail_secure'
      };
    }
  }, [invoke]);

  const requireStepUp = useCallback(async (
    actionType: string, 
    onVerified: () => void | Promise<void>
  ): Promise<void> => {
    const result = await checkStepUpRequired(actionType);
    
    if (!result) {
      // Failed to check, fail secure
      toast.error("Security check failed. Please try again.");
      return;
    }

    if (!result.requires_step_up) {
      // No step-up required, execute action immediately
      await onVerified();
      return;
    }

    // Step-up required, show dialog
    console.log(`Step-up authentication required for ${actionType}:`, result.reason);
    setCurrentActionType(actionType);
    setStepUpData(result);
    setPendingAction(() => onVerified);
    setShowStepUpDialog(true);
  }, [checkStepUpRequired]);

  // Execute pending action when dialog closes successfully
  const handleVerified = useCallback(async () => {
    if (pendingAction) {
      await pendingAction();
      setPendingAction(null);
    }
    setShowStepUpDialog(false);
    setCurrentActionType('');
    setStepUpData(null);
  }, [pendingAction]);

  return {
    checkStepUpRequired,
    requireStepUp,
    showStepUpDialog,
    setShowStepUpDialog,
    currentActionType,
    stepUpData,
    handleVerified
  };
};
